import React from 'react';

const DEFAULT_PROCESSING_STEPS = [
  {
    label: 'Started processing',
    aliases: [
      'started processing',
      'processing started',
      'start processing',
      'upload received',
      'job started',
    ],
  },
  {
    label: 'Downloading video from S3',
    aliases: [
      'downloading video from s3',
      'downloaded video from s3',
      'download from s3',
      'downloading from s3',
      'downloaded from s3',
    ],
  },
  {
    label: 'Re-encoding video',
    aliases: [
      're-encoding video if needed',
      'reencoding video if needed',
      're-encoding video',
      'reencoding video',
      'encoding video',
      'video re-encoded',
      'video reencoded',
    ],
  },
  {
    label: 'Checking audio quality',
    aliases: [
      'checking audio quality',
      'audio quality check',
      'checking audio',
      'audio check',
      'audio quality verified',
    ],
  },
  {
    label: 'Converting video to FLAC',
    aliases: [
      'converting video to flac',
      'converted video to flac',
      'flac conversion',
      'converting to flac',
      'converted to flac',
    ],
  },
  {
    label: 'Speech recognition in progress',
    aliases: [
      'speech recognition in progress',
      'speech recognition',
      'recognizing speech',
      'transcribing speech',
      'transcription in progress',
    ],
  },
  {
    label: 'Parsing transcription',
    aliases: [
      'parsing transcription',
      'parsed transcription',
      'parsing transcriptions',
      'transcription parsing',
      'processing transcription',
      'transcriptions parsed successfully',
    ],
  },
  {
    label: 'Formatting subtitles',
    aliases: [
      'formatting subtitles',
      'formatted subtitles',
      'subtitle formatting',
      'format subtitles',
    ],
  },
  {
    label: 'Translating subtitles',
    aliases: [
      'translating subtitles',
      'translated subtitles',
      'subtitle translation',
      'translate subtitles',
      'translation complete',
    ],
  },
  {
    label: 'Merging subtitles with video',
    aliases: [
      'merging subtitles with video',
      'merged subtitles with video',
      'subtitle merging',
      'merging subtitles',
      'merged subtitles',
      'burning subtitles',
      'burned subtitles',
    ],
  },
  {
    label: 'Verifying output',
    aliases: [
      'verifying output',
      'verified output',
      'output verification',
      'checking output',
      'final verification',
      'storing output',
      'stored output',
      'storing merged video',
    ],
  },
  {
    label: 'Finished processing',
    aliases: [
      'finished processing',
      'processing finished',
      'complete',
      'completed',
      'done',
    ],
  },
];

const ProcessingTimeline = ({
  loadingSteps,
  isUploading,
  isProcessingFinished,
  selectedFile,
}) => {
  const cleanStepText = (step) => {
    const rawText =
      typeof step === 'string'
        ? step
        : step?.label ||
          step?.message ||
          step?.status ||
          step?.title ||
          'Processing step';

    return String(rawText)
      .replace(/^✔️\s*/u, '')
      .replace(/^✅\s*/u, '')
      .replace(/^❌\s*/u, '')
      .trim()
      .toLowerCase();
  };

  const completedStepTexts = loadingSteps.map(cleanStepText);

  const getMatchedStepIndex = (completedText) => {
    return DEFAULT_PROCESSING_STEPS.findIndex((step) => {
      const possibleMatches = [step.label, ...step.aliases].map((value) =>
        value.toLowerCase()
      );

      return possibleMatches.some((matchText) => {
        return (
          completedText === matchText ||
          completedText.includes(matchText) ||
          matchText.includes(completedText)
        );
      });
    });
  };

  const highestConfirmedStepIndex = completedStepTexts.reduce(
    (highestIndex, completedText) => {
      const matchedIndex = getMatchedStepIndex(completedText);

      if (matchedIndex > highestIndex) {
        return matchedIndex;
      }

      return highestIndex;
    },
    isUploading || loadingSteps.length > 0 ? 0 : -1
  );

  const finalHighestConfirmedStepIndex = isProcessingFinished
    ? DEFAULT_PROCESSING_STEPS.length - 1
    : highestConfirmedStepIndex;

  const hasStepCompleted = (index) => {
    return index <= finalHighestConfirmedStepIndex;
  };

  const completedCount = DEFAULT_PROCESSING_STEPS.filter((step, index) =>
    hasStepCompleted(index)
  ).length;

  const currentStepIndex = isProcessingFinished
    ? DEFAULT_PROCESSING_STEPS.length - 1
    : Math.min(
        Math.max(finalHighestConfirmedStepIndex + 1, 0),
        DEFAULT_PROCESSING_STEPS.length - 1
      );

  const statusLabel = isProcessingFinished
    ? 'Complete'
    : isUploading
      ? 'Running'
      : selectedFile
        ? 'Ready'
        : 'Waiting';

  const progressPercent = isProcessingFinished
    ? 100
    : Math.round((completedCount / DEFAULT_PROCESSING_STEPS.length) * 100);

  return (
    <div className="relative overflow-hidden rounded-3xl border border-cyan-300/15 bg-slate-950/72 p-4 shadow-2xl shadow-slate-950/40 backdrop-blur-xl">
      <div className="pointer-events-none absolute -right-10 top-0 h-32 w-32 rounded-full bg-cyan-300/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-10 bottom-0 h-32 w-32 rounded-full bg-amber-300/10 blur-3xl" />

      <div className="relative z-10 mb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.26em] text-cyan-300">
              Live Tracker
            </p>

            <h3 className="mt-1 text-lg font-black text-white">
              Processing Timeline
            </h3>
          </div>

          <div
            className={[
              'shrink-0 rounded-full border px-3 py-1 text-xs font-black uppercase tracking-[0.14em]',
              isProcessingFinished
                ? 'border-emerald-300/30 bg-emerald-400/10 text-emerald-200'
                : isUploading
                  ? 'border-cyan-300/30 bg-cyan-400/10 text-cyan-100'
                  : selectedFile
                    ? 'border-amber-300/30 bg-amber-400/10 text-amber-100'
                    : 'border-slate-400/20 bg-slate-400/10 text-slate-300',
            ].join(' ')}
          >
            {statusLabel}
          </div>
        </div>

        {selectedFile ? (
          <p className="mt-4 truncate rounded-2xl border border-white/10 bg-slate-950/60 px-3 py-2 text-xs font-semibold text-slate-300">
            {selectedFile.name}
          </p>
        ) : (
          <p className="mt-4 rounded-2xl border border-dashed border-cyan-300/15 bg-slate-950/45 px-3 py-2 text-xs font-semibold leading-5 text-slate-500">
            Choose a video to begin tracking.
          </p>
        )}

        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between text-xs font-bold text-slate-400">
            <span>Progress</span>
            <span>{progressPercent}%</span>
          </div>

          <div className="h-2 overflow-hidden rounded-full bg-slate-800/90">
            <div
              className={[
                'h-full rounded-full transition-all duration-700',
                isProcessingFinished ? 'bg-emerald-300' : 'bg-cyan-300',
              ].join(' ')}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      <div className="relative z-10 space-y-3">
        {DEFAULT_PROCESSING_STEPS.map((step, index) => {
          const isDone = hasStepCompleted(index);
          const isCurrent =
            !isProcessingFinished &&
            isUploading &&
            index === currentStepIndex;

          const isWaiting = !isDone && !isCurrent;

          return (
            <div
              key={step.label}
              className={[
                'relative overflow-hidden rounded-2xl border px-4 py-3 shadow-lg transition',
                isDone
                  ? 'border-emerald-300/25 bg-emerald-400/10 shadow-emerald-950/20'
                  : isCurrent
                    ? 'border-cyan-300/35 bg-cyan-400/10 shadow-cyan-950/30'
                    : 'border-white/10 bg-slate-900/45 opacity-55 shadow-slate-950/20',
              ].join(' ')}
            >
              {isCurrent && (
                <>
                  <div className="pointer-events-none absolute -right-6 top-1/2 h-16 w-16 -translate-y-1/2 rounded-full bg-cyan-300/14 blur-2xl" />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-cyan-300/8 via-transparent to-amber-300/5" />
                </>
              )}

              <div className="relative z-10 flex items-start gap-3">
                <div
                  className={[
                    'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-black shadow-lg',
                    isDone
                      ? 'border-emerald-200/50 bg-emerald-300/20 text-emerald-100 shadow-emerald-300/30'
                      : isCurrent
                        ? 'border-cyan-200/50 bg-cyan-300/20 text-cyan-100 shadow-cyan-300/30'
                        : 'border-slate-500/35 bg-slate-800/50 text-slate-500 shadow-slate-950/20',
                  ].join(' ')}
                >
                  {isDone ? (
                    '✓'
                  ) : isCurrent ? (
                    <span className="relative flex h-4 w-4 items-center justify-center">
                      <span className="absolute h-4 w-4 animate-spin rounded-full border-2 border-cyan-100/20 border-t-cyan-100" />
                      <span className="absolute h-2 w-2 rounded-full bg-cyan-200/70 shadow-[0_0_10px_rgba(103,232,249,0.75)]" />
                    </span>
                  ) : (
                    ''
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <p
                    className={[
                      'text-sm font-bold leading-5',
                      isDone
                        ? 'text-emerald-100'
                        : isCurrent
                          ? 'text-white'
                          : 'text-slate-400',
                    ].join(' ')}
                  >
                    {step.label}
                  </p>

                  {isCurrent && (
                    <p className="mt-1 text-xs font-semibold text-cyan-200/80">
                      Working on this step
                    </p>
                  )}

                  {isDone && step.label === 'Finished processing' && (
                    <p className="mt-1 text-xs font-semibold text-emerald-300/90">
                      Finished successfully
                    </p>
                  )}

                  {isWaiting && (
                    <p className="mt-1 text-xs font-semibold text-slate-500">
                      Waiting
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProcessingTimeline;